import * as AbsintheSocket from '@absinthe/socket';
import { createAbsintheSocketLink } from '@absinthe/socket-apollo-link';
import { ApolloLink } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { createLink } from 'apollo-absinthe-upload-link';
import authTokenUtil from '../../account/authTokenUtil';
import visitorAuthTokenUtil from '../../account/visitorAuthTokenUtil';
import SocketWithBackoffReconnect from '../../utils/SocketWithBackoffReconnect';

const { host } = window.location;
const endpoint = `//${host}/graphql`;

const abortController = new AbortController();

// Needed by LogRocket https://docs.logrocket.com/docs/graphql#apollo-client-with-graphql
const fetcher = (...args: Parameters<Window['fetch']>) => {
  return window.fetch(...args);
};

// We need to pass `fetch`, otherwise `apollo-absinthe-upload-link` will
// assume that `fetch` is not available and use `rxjs`'s `Observable`.
// See https://github.com/bytewitchcraft/apollo-absinthe-upload-link/issues/18
const httpLink = createLink({
  fetch: fetcher,
  fetchOptions: { signal: abortController.signal },
  uri: `https:${endpoint}`,
});

export default () => {
  const socket = new SocketWithBackoffReconnect(`wss:${endpoint}`);
  const { channel } = socket;
  const authToken = authTokenUtil.get() ?? visitorAuthTokenUtil.get();
  socket.channel = (topic: string, params?: object | undefined) => {
    if (topic === '__absinthe__:control') {
      return channel.call(socket, topic, {
        guardian_token: authToken,
      });
    }
    return channel.call(socket, topic, params);
  };

  const subscriptionLink = createAbsintheSocketLink(
    AbsintheSocket.create(socket),
  );

  const cleanup = () => {
    socket.disconnect();
  };

  return [
    ApolloLink.split(
      ({ query }) => {
        const def = getMainDefinition(query);
        return (
          def.kind === 'OperationDefinition' && def.operation === 'subscription'
        );
      },
      // @ts-expect-error
      subscriptionLink,
      httpLink,
    ),
    cleanup,
  ] as const;
};
